import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SubPage } from "@/components/sub-page";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/comunicaciones/newsletter/")({
  head: () => ({
    meta: [
      { title: "Newsletter | Mine Class" },
      { name: "description", content: "Todas las ediciones del newsletter Mine Class: novedades sobre capacitación, tecnología y actualidad de la industria minera." },
      { property: "og:title", content: "Newsletter Mine Class" },
      { property: "og:description", content: "Explora todas nuestras ediciones del newsletter con novedades del ecosistema minero, capacitación y tecnología." },
      { property: "og:url", content: "/comunicaciones/newsletter" },
    ],
    links: [{ rel: "canonical", href: "/comunicaciones/newsletter" }],
  }),
  component: Page,
});

type NL = { id: string; subject: string; sent_at: string | null; created_at: string; cover?: string | null };

// Usa la primera imagen del contenido como portada de la edición
function coverFrom(html: string | null): string | null {
  if (!html) return null;
  const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return m ? m[1] : null;
}

function Page() {
  const [items, setItems] = useState<NL[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("newsletters")
        .select("id,subject,sent_at,created_at,body_html")
        .eq("status", "sent")
        .order("sent_at", { ascending: false });
      setItems(
        ((data ?? []) as (NL & { body_html: string | null })[]).map((n) => ({
          ...n,
          cover: coverFrom(n.body_html),
        })),
      );
    })();
  }, []);

  return (
    <SubPage
      section="comunicaciones"
      eyebrow="Comunicaciones"
      title="Newsletter"
      intro="Todas nuestras ediciones en un solo lugar."
    >
      <SubscribeForm />

      {items.length === 0 ? (
        <p className="text-[var(--color-on-surface-variant)]">Aún no hay ediciones publicadas.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 not-prose">
          {items.map((n) => (
            <Link
              key={n.id}
              to="/comunicaciones/newsletter/$id"
              params={{ id: n.id }}
              className="text-left bg-white border border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)] transition-colors block overflow-hidden"
            >
              {n.cover ? (
                <img
                  src={n.cover}
                  alt={`Portada: ${n.subject}`}
                  loading="lazy"
                  className="w-full h-48 object-cover bg-[var(--color-surface-container-low)]"
                />
              ) : (
                <div className="w-full h-48 bg-[var(--color-brand-purple)]/10" />
              )}
              <div className="p-6">
              <div className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold">Newsletter</div>
              <h3 className="text-lg font-bold text-[var(--color-brand-purple)] mt-1">{n.subject}</h3>
              {n.sent_at && (
                <p className="text-xs text-[var(--color-on-surface-variant)] mt-2">
                  {new Date(n.sent_at).toLocaleDateString("es-CL", { dateStyle: "long" })}
                </p>
              )}
              <span className="inline-block mt-3 text-sm font-bold text-[var(--color-brand-coral)]">Leer edición →</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </SubPage>
  );
}

function SubscribeForm() {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const fd = new FormData(e.currentTarget);
      const full_name = String(fd.get("full_name") ?? "").trim();
      const email = String(fd.get("email") ?? "").trim().toLowerCase();
      if (!full_name || !email) throw new Error("Completa nombre y correo.");
      const { error } = await supabase.from("newsletter_subscribers").insert({ full_name, email });
      if (error && !/duplicate|unique/i.test(error.message)) throw error;
      setOk(true);
      (e.target as HTMLFormElement).reset();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "No se pudo suscribir.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="not-prose bg-[var(--color-brand-purple)] text-white p-8 md:p-10 mb-10">
      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/70 font-bold">Suscríbete</div>
          <h3 className="text-2xl font-bold mt-1">Recibe nuestro newsletter</h3>
          <p className="text-white/80 mt-2 text-sm">
            Novedades del ecosistema minero, capacitación y tecnología directo en tu correo.
          </p>
        </div>
        {ok ? (
          <div
            className="bg-white/10 border border-white/20 p-4 text-sm"
            role="status"
            aria-live="polite"
          >
            ¡Gracias! Te has suscrito correctamente. 📬
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="flex flex-col gap-2 w-full md:w-96"
            aria-label="Suscripción al newsletter"
            aria-busy={busy}
            aria-describedby={err ? "newsletter-error" : undefined}
          >
            <label htmlFor="nl-full-name" className="sr-only">Nombre completo</label>
            <input
              id="nl-full-name"
              name="full_name"
              autoComplete="name"
              aria-required="true"
              required
              placeholder="Nombre*"
              className="bg-white/10 border border-white/20 focus:border-[var(--color-brand-coral)] text-white placeholder-white/50 px-4 py-3 outline-none"
            />
            <label htmlFor="nl-email" className="sr-only">Correo electrónico</label>
            <input
              id="nl-email"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              aria-required="true"
              aria-invalid={!!err}
              required
              placeholder="Correo*"
              className="bg-white/10 border border-white/20 focus:border-[var(--color-brand-coral)] text-white placeholder-white/50 px-4 py-3 outline-none"
            />
            <div aria-live="assertive" aria-atomic="true" className="min-h-[1rem]">
              {err && (
                <p id="newsletter-error" role="alert" className="text-sm text-[var(--color-brand-coral)] bg-white/10 px-3 py-2">
                  {err}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={busy}
              aria-disabled={busy}
              className="py-3 bg-[var(--color-brand-coral)] text-white font-bold uppercase tracking-widest hover:brightness-110 disabled:opacity-50"
            >
              {busy ? "Enviando..." : "Suscribirme"}
            </button>
          </form>
        )}

      </div>
    </div>
  );
}
